// All portfolio content lives here. Frontend-only mock layer.
// Will be swapped for API calls in the backend phase.

export const personal = {
  name: "Harsh Tripathi",
  firstName: "Harsh",
  lastName: "Tripathi",
  role: "Lead Engineer",
  subtitle: "Backend · Cloud · Distributed Systems",
  company: "HCLTech",
  since: "Aug 2025",
  taglines: [
    "Lead Engineer.",
    "Backend Architect.",
    "AWS Certified.",
    "Cloud-Native Developer.",
  ],
  location: "Lucknow, Uttar Pradesh — India",
  timezone: "IST · UTC+5:30",
  email: "harshtripathiyt@gmail.com",
  phone: "+91 80813 67265",
  linkedin: "https://www.linkedin.com/in/harshtripathi-in",
  github: "https://github.com/harshtripathi-in",
  resumeUrl: "../../DOC/Harsh_Tripathi_Resume_.pdf",
  status: "Open to new opportunities",
  photoUrl:
    "../../DOC/profile.jpg",
  short:
    "Lead Engineer at HCLTech with 3+ years of experience building and scaling secure, microservice-based enterprise platforms. I work across Node.js, Java / Spring Boot and AWS — with a focus on performance, reliability and compliance.",
  longIntro: [
    "I'm a backend-focused engineer who enjoys the quiet craft of making systems fast, observable and boring in the best possible way.",
    "Currently Lead Engineer at HCLTech since August 2025 — owning services that process more than a million masked financial records every day, optimising Node.js and Spring Boot microservices, tuning PostgreSQL and MongoDB, and keeping uptime north of 99.9%.",
    "I'm AWS Certified, currently pursuing an MCA in Artificial Intelligence at Amity University, and increasingly interested in the intersection of distributed systems and applied AI.",
  ],
};

// Highlight badges used across the hero / key areas.
export const highlights = [
  {
    label: "Role",
    value: "Lead Engineer",
    meta: "HCLTech · Architecting high-performance backend systems"
  },
  {
    label: "Processing Capacity",
    value: "1M+ txns/day",
    meta: "Distributed financial systems with optimized concurrency"
  },
  {
    label: "System Stability",
    value: "99.9% SLA",
    meta: "Fault-tolerant services with minimal downtime"
  },
  {
    label: "Cloud & Infra",
    value: "AWS Certified",
    meta: "Scalable deployments, monitoring & cost optimization"
  },
];

export const stats = [
  { label: "Experience", value: "4+ yrs" },
  { label: "Load Handling", value: "1M+ daily volume" },
  { label: "Service Health", value: "99.9% SLA" },
  { label: "Efficiency Gains", value: "↑40%" },
];

export const navLinks = [
  { label: "Home", path: "/", index: "01" },
  { label: "About", path: "/about", index: "02" },
  { label: "Skills", path: "/skills", index: "03" },
  { label: "Work", path: "/work", index: "04" },
  { label: "Journal", path: "/journal", index: "05" },
  { label: "Links", path: "/links", index: "06" },
  { label: "Contact", path: "/contact", index: "07" },
  { label: "Discussion", path: "/discussion", index: "08" }
];

export const timeline = [
  {
    period: "Aug 2025 — Present",
    role: "Lead Engineer",
    company: "HCLTech",
    location: "India · Hybrid",
    bullets: [
      "Promoted to Lead Engineer — now driving architecture and delivery for business-critical financial platforms.",
      "Leading a pod of engineers across microservices, data and observability tracks.",
      "Owning roadmap for reliability, performance and compliance of platforms serving 1M+ daily transactions.",
      "Mentoring engineers on backend, cloud and code-review practices.",
    ],
  },
  {
    period: "Jan 2023 — Aug 2025",
    role: "Software Engineer",
    company: "HCLTech",
    location: "India · Hybrid",
    bullets: [
      "Engineered microservices processing 1M+ masked financial transactions daily for a global payments client.",
      "Architected high-performance REST APIs in Node.js, Java and Spring Boot — sustaining 99.9% service uptime.",
      "Optimised PostgreSQL and MongoDB query paths, reducing average response time by 22%.",
      "Automated performance and regression pipelines, lowering post-release defects by 30%.",
    ],
  },
  {
    period: "Aug 2022 — Jan 2023",
    role: "Software Engineering Intern",
    company: "HCLTech",
    location: "India",
    bullets: [
      "Built backend services in Express.js, improving API consistency and maintainability.",
      "Authored Swagger API documentation that cut developer onboarding time by ~20%.",
      "Supported MongoDB and MySQL data operations for internal platforms.",
    ],
  },
  {
    period: "Jan 2022 — Aug 2022",
    role: "Software Engineering Trainee",
    company: "HCLTech",
    location: "India",
    bullets: [
      "Structured training across Java, Spring Boot, Angular, HTML and CSS.",
      "Shipped proof-of-concept apps and contributed to cloud integration labs.",
    ],
  },
];

export const education = [
  {
    period: "2025 — Present",
    degree: "MCA · Artificial Intelligence",
    school: "Amity University",
    note: "Currently pursuing",
  },
  {
    period: "2023 — 2025",
    degree: "BCA · Computer Science",
    school: "Amity University, Noida",
    note: "CGPA 8.4 / 10",
  },
];

export const certifications = [
  { name: "AWS Certified Cloud Practitioner", issuer: "Amazon Web Services" },
  { name: "Generative AI for Executives", issuer: "OutSkill" },
  { name: "AI For All", issuer: "HCLTech" },
];

export const skills = {
  Backend: [
    { name: "Node.js", level: 95 },
    { name: "Java", level: 88 },
    { name: "Spring Boot", level: 88 },
    { name: "Express.js", level: 90 },
    { name: "Python", level: 70 },
  ],
  Frontend: [
    { name: "React", level: 78 },
    { name: "Angular", level: 72 },
    { name: "Vue.js", level: 65 },
  ],
  Databases: [
    { name: "PostgreSQL", level: 90 },
    { name: "MongoDB", level: 88 },
    { name: "MySQL", level: 80 },
    { name: "Redis", level: 82 },
  ],
  "Cloud & DevOps": [
    { name: "AWS", level: 85 },
    { name: "Jenkins", level: 75 },
    { name: "Git / GitHub", level: 92 },
    { name: "Bitbucket", level: 80 },
  ],
  "Testing & Tools": [
    { name: "JMeter", level: 82 },
    { name: "BlazeMeter", level: 78 },
    { name: "Jest", level: 80 },
    { name: "Swagger", level: 90 },
    { name: "Postman", level: 92 },
  ],
  Practices: [
    { name: "Microservices", level: 90 },
    { name: "REST APIs", level: 95 },
    { name: "CI / CD", level: 82 },
    { name: "Agile", level: 88 },
  ],
};

export const projects = [
  {
    id: "edmp",
    title: "Enterprise Data Masking Platform",
    client: "Global Payments Client",
    year: "2024",
    category: "Backend",
    summary:
      "Secure microservice platform handling 1M+ masked financial records per day with role-based access and audit-grade observability.",
    bullets: [
      "Designed microservices sustaining 99.9% uptime under peak load.",
      "Increased throughput by 38% using Redis caching + async workflows.",
      "Enforced RBAC to meet audit & compliance requirements.",
      "Reduced incident resolution time by 30% via improved observability.",
    ],
    stack: ["Node.js", "Spring Boot", "PostgreSQL", "Redis", "AWS"],
    links: { live: null, repo: null },
    cover: "EDMP",
  },
  {
    id: "mf-nexus",
    title: "MF Nexus",
    client: "Mutual Fund Investment Platform",
    year: "2024",
    category: "Full-stack",
    summary:
      "Portfolio management and SIP simulation platform supporting 5,000+ daily operations with hardened authentication.",
    bullets: [
      "Designed portfolio + SIP simulation modules for 5k+ daily ops.",
      "Cut API latency by 28% through caching and query optimisation.",
      "Hardened auth with JWT + session protection.",
    ],
    stack: ["Node.js", "React", "MongoDB", "Redis"],
    links: { live: null, repo: "https://github.com/harshtripathi-in" },
    cover: "MFN",
  },
  {
    id: "serotonin",
    title: "Serotonin",
    client: "Music Streaming Application",
    year: "2023",
    category: "Full-stack",
    summary:
      "Playlists, search and real-time streaming for concurrent users with a tuned data layer for snappy navigation.",
    bullets: [
      "Built playlists, search and real-time streaming.",
      "Reduced page load time by 25% via data-access tuning.",
    ],
    stack: ["Angular", "Spring Boot", "MySQL"],
    links: { live: null, repo: "https://github.com/harshtripathi-in" },
    cover: "SRT",
  },
  {
    id: "taskflow",
    title: "TaskFlow",
    client: "Workflow & Automation Engine",
    year: "2024",
    category: "Backend",
    summary:
      "Event-driven automation engine processing 10k+ tasks daily with audit logging, retries and deduplication.",
    bullets: [
      "Event-driven engine processing 10k+ tasks / day.",
      "Audit logging + RBAC for enterprise security.",
      "Improved task reliability by 35% via retries + dedup.",
    ],
    stack: ["Node.js", "Redis", "AWS Lambda", "PostgreSQL"],
    links: { live: null, repo: "https://github.com/harshtripathi-in" },
    cover: "TF",
  },
];

export const projectCategories = ["All", "Backend", "Full-stack"];


// Replace the existing `blogs` array in your mock.js with this.
// Each post has a `content` array of blocks — paragraph, h2, blockquote, list, code.

export const blogs = [
//   {
//     id: "scaling-node-microservices",
//     title: "Scaling Node.js microservices to a million requests a day",
//     excerpt:
//       "Notes from tuning a real-world financial microservice — connection pools, backpressure, caching layers and the small decisions that compound.",
//     date: "2025-03-18",
//     readTime: "9 min read",
//     category: "Backend",
//     tags: ["Node.js", "Performance", "Microservices"],
//     content: [
//       {
//         type: "p",
//         text: "When our financial microservice started processing over a million masked transactions daily, the cracks appeared fast. Response times crept up, connection pool exhaustion errors surfaced at peak hours, and our on-call rotation got very busy. Here's what we actually fixed — no hand-waving, just the specific changes that moved the needle.",
//       },
//       { type: "h2", text: "The baseline problem" },
//       {
//         type: "p",
//         text: "The service was built on Node.js with Express, backed by PostgreSQL and a Redis cache. Under light load it was fine. Under 50k+ requests per hour it fell apart. Our first step was proper observability — we couldn't fix what we couldn't measure. We added structured logging with correlation IDs, Prometheus metrics on every route, and P95/P99 latency dashboards.",
//       },
//       {
//         type: "p",
//         text: "The first thing the dashboards told us: our PostgreSQL connection pool was sized at the default (10 connections). We were running 4 Node.js instances. That's 40 total connections fighting over every hot query. We bumped it to `Math.ceil(cpuCount * 2.5)` per instance and saw our P95 drop by 18% immediately.",
//       },
//       { type: "h2", text: "Backpressure and queue depth" },
//       {
//         type: "p",
//         text: "Node.js's async event loop is a double-edged sword. It handles concurrency beautifully — right up until you have more in-flight work than your downstream systems can absorb. We were accepting requests faster than PostgreSQL could serve them, building up a silent backlog in memory.",
//       },
//       {
//         type: "blockquote",
//         text: "Accepting a request is a promise. If you can't keep it, fail fast — don't queue forever.",
//       },
//       {
//         type: "p",
//         text: "We added explicit concurrency limits using `p-limit` on our database call layer, and circuit breakers via `opossum`. When the DB was under pressure, we returned 503s immediately rather than letting the queue grow unbounded. Error rate went up briefly — but P99 latency came down by 40% and never climbed back.",
//       },
//       { type: "h2", text: "Redis caching for hot paths" },
//       {
//         type: "p",
//         text: "About 60% of our read traffic was hitting the same aggregate queries — daily totals, status summaries — that changed at most once per minute. We added a Redis cache with a 60-second TTL on those endpoints. Cache hit rate stabilised at ~73%, which translated directly to 38% less load on PostgreSQL.",
//       },
//       {
//         type: "code",
//         text: `const cacheKey = \`summary:\${clientId}:\${date}\`;
// const cached = await redis.get(cacheKey);
// if (cached) return JSON.parse(cached);

// const result = await db.query(summaryQuery, [clientId, date]);
// await redis.setex(cacheKey, 60, JSON.stringify(result));
// return result;`,
//       },
//       { type: "h2", text: "What actually compounded" },
//       {
//         type: "p",
//         text: "No single fix was the silver bullet. The connection pool tuning, backpressure controls, and caching each contributed maybe 15-40% on their own metric. Together, sustained throughput went from ~600 req/s to 1,100 req/s with P95 under 80ms. Uptime hit 99.9% and stayed there.",
//       },
//       {
//         type: "list",
//         items: [
//           "Tune connection pool size relative to CPU cores and instance count — defaults are rarely right.",
//           "Add explicit concurrency limits and circuit breakers before you need them.",
//           "Cache aggressively on read-heavy aggregates — even 60s TTL makes a material difference.",
//           "Instrument first. Every optimisation listed above was found via metrics, not intuition.",
//         ],
//       },
//     ],
//   },
//   {
//     id: "postgres-query-wins",
//     title: "Five PostgreSQL query rewrites that cut our latency by 22%",
//     excerpt:
//       "A practical tour of the index strategies, window functions and explain-plan reads that made our hottest endpoints noticeably faster.",
//     date: "2025-01-09",
//     readTime: "7 min read",
//     category: "Databases",
//     tags: ["PostgreSQL", "Optimisation"],
//     content: [
//       {
//         type: "p",
//         text: "Our financial dashboard endpoint was hitting 480ms P95 under load. After a week of reading EXPLAIN (ANALYZE, BUFFERS) output and testing rewrites in a staging environment, we got it to 112ms. Here are the five changes that did it.",
//       },
//       { type: "h2", text: "1. Correlated subquery → window function" },
//       {
//         type: "p",
//         text: "We had a correlated subquery computing a running balance for each transaction row. It executed once per row — O(n²) effectively. Replacing it with a window function (SUM OVER PARTITION BY ... ORDER BY) made it a single pass. 4× faster on a 100k-row result set.",
//       },
//       {
//         type: "code",
//         text: `-- Before (correlated subquery — slow)
// SELECT t.id, t.amount,
//   (SELECT SUM(amount) FROM txns t2
//    WHERE t2.account_id = t.account_id AND t2.created_at <= t.created_at) AS running_total
// FROM txns t;

// -- After (window function — fast)
// SELECT id, amount,
//   SUM(amount) OVER (PARTITION BY account_id ORDER BY created_at) AS running_total
// FROM txns;`,
//       },
//       { type: "h2", text: "2. Partial index on status" },
//       {
//         type: "p",
//         text: "About 80% of our queries filtered by status != 'completed'. We had a full index on status, but PostgreSQL was often choosing a seq scan anyway because 'completed' rows made up 90% of the table. A partial index WHERE status != 'completed' indexed only the 10% of rows that mattered.",
//       },
//       {
//         type: "code",
//         text: `CREATE INDEX idx_txns_active_status
// ON txns (status, created_at)
// WHERE status != 'completed';`,
//       },
//       { type: "h2", text: "3. Covering index to avoid heap fetches" },
//       {
//         type: "p",
//         text: "Our hottest SELECT fetched id, amount, created_at, status. The index existed on (account_id, created_at) but PostgreSQL still had to fetch the heap for amount and status. Adding those columns to the index as INCLUDEd columns eliminated the heap fetch entirely — Index Only Scan in the plan.",
//       },
//       { type: "h2", text: "4. OR → UNION ALL" },
//       {
//         type: "p",
//         text: "A query with WHERE status = 'pending' OR status = 'failed' was doing a bitmap heap scan across the full table. Rewriting as UNION ALL (one SELECT per status) let the planner use the index on each branch independently. 2.3× faster.",
//       },
//       { type: "h2", text: "5. Per-transaction work_mem for analytics" },
//       {
//         type: "p",
//         text: "Global work_mem was conservatively set at 4MB. Our analytics queries sort large intermediate result sets and were spilling to disk constantly — visible in the plan as 'Sort Method: external merge Disk'. Setting SET LOCAL work_mem = '64MB' for those specific queries eliminated the disk spill. We didn't raise it globally because that would multiply by max_connections and exhaust RAM.",
//       },
//       {
//         type: "blockquote",
//         text: "EXPLAIN (ANALYZE, BUFFERS) is the most underused tool in most backend engineers' kits.",
//       },
//       {
//         type: "p",
//         text: "Total result: P95 on that endpoint went from 480ms to 112ms. No schema changes, no hardware upgrades — just reading the query plans carefully and removing the waste they revealed.",
//       },
//     ],
//   },
//   {
//     id: "spring-boot-observability",
//     title: "Observability that actually pays off in Spring Boot services",
//     excerpt:
//       "Metrics, tracing and structured logs — a minimal but effective setup that dropped our incident resolution time by 30%.",
//     date: "2024-11-22",
//     readTime: "11 min read",
//     category: "Backend",
//     tags: ["Java", "Spring Boot", "Observability"],
//     content: [
//       {
//         type: "p",
//         text: "Observability gets talked about a lot and implemented badly even more. Most teams bolt on logging as an afterthought and wonder why debugging production incidents still takes hours. Here's the minimal setup that actually reduced our mean time to resolution by 30%.",
//       },
//       { type: "h2", text: "The three pillars — but with focus" },
//       {
//         type: "p",
//         text: "Logs, metrics and traces are the canonical three pillars. In practice, for a Spring Boot microservice, you get the most leverage in this order: structured logs first, then RED metrics on every service boundary, then distributed tracing last (it's the most effort for the least immediate return).",
//       },
//       { type: "h2", text: "Structured logging with correlation IDs" },
//       {
//         type: "p",
//         text: "Switch from plain text logs to JSON immediately. Add a correlation ID (X-Correlation-ID header) to every inbound request and propagate it to every log line and outbound call. When an incident happens, you grep one ID and get the full request journey across services.",
//       },
//       {
//         type: "code",
//         text: `// MDC filter — put this in your filter chain
// public class CorrelationIdFilter extends OncePerRequestFilter {
//     @Override
//     protected void doFilterInternal(HttpServletRequest req,
//                                     HttpServletResponse res,
//                                     FilterChain chain) throws IOException, ServletException {
//         String correlationId = Optional
//             .ofNullable(req.getHeader("X-Correlation-ID"))
//             .orElse(UUID.randomUUID().toString());
//         MDC.put("correlationId", correlationId);
//         res.setHeader("X-Correlation-ID", correlationId);
//         try { chain.doFilter(req, res); }
//         finally { MDC.clear(); }
//     }
// }`,
//       },
//       { type: "h2", text: "RED metrics on every boundary" },
//       {
//         type: "p",
//         text: "Rate, Errors, Duration — track these at every service boundary: inbound HTTP, outbound HTTP calls, database queries, cache operations. Spring Boot Actuator + Micrometer gives you most of this for free with @Timed and the built-in datasource metrics. The key is making sure your dashboards show P95 and P99, not just averages.",
//       },
//       {
//         type: "blockquote",
//         text: "Averages hide the pain. Your worst 1% of users define your actual reliability.",
//       },
//       { type: "h2", text: "Alerting on symptoms not causes" },
//       {
//         type: "p",
//         text: "Alert on error rate > 1% and P99 latency > threshold for your SLA. Don't alert on CPU or heap usage directly — those are causes, and you'll spend all your time chasing red herrings. When the symptom alert fires, the structured logs and metrics give you the cause.",
//       },
//       {
//         type: "list",
//         items: [
//           "Structured JSON logs + correlation IDs — do this first, everything else depends on it.",
//           "RED metrics at every boundary via Micrometer — 30 minutes to set up, pays back in every incident.",
//           "P95/P99 dashboards, not averages — averages lie.",
//           "Alert on symptoms (error rate, latency) not causes (CPU, memory).",
//           "Add distributed tracing only once the above is solid — it's the cherry on top, not the foundation.",
//         ],
//       },
//     ],
//   },
//   {
//     id: "aws-lambda-patterns",
//     title: "Four AWS Lambda patterns I keep reaching for",
//     excerpt:
//       "Event-driven workflows, idempotency, retries and cold-start mitigations — patterns from building an automation engine on Lambda.",
//     date: "2024-09-04",
//     readTime: "8 min read",
//     category: "Cloud",
//     tags: ["AWS", "Serverless"],
//     content: [
//       {
//         type: "p",
//         text: "After building an event-driven automation engine on AWS Lambda that processes 10,000+ tasks per day with zero lost events over six months, these are the four patterns I reach for on every Lambda project — in the order I apply them.",
//       },
//       { type: "h2", text: "1. Idempotency keys — non-negotiable" },
//       {
//         type: "p",
//         text: "Lambda retries on failure. SQS delivers at-least-once. If your function isn't idempotent, you will process events multiple times in production. The cleanest approach is a DynamoDB conditional write at the start of every handler — check if this event ID has been processed, write it if not, skip if it has.",
//       },
//       {
//         type: "code",
//         text: `const params = {
//   TableName: 'IdempotencyKeys',
//   Item: { eventId: event.id, processedAt: Date.now() },
//   ConditionExpression: 'attribute_not_exists(eventId)',
// };
// try {
//   await dynamodb.put(params).promise();
// } catch (e) {
//   if (e.code === 'ConditionalCheckFailedException') return; // already processed
//   throw e;
// }`,
//       },
//       { type: "h2", text: "2. Dead-letter queues on everything" },
//       {
//         type: "p",
//         text: "Configure an SQS DLQ on every Lambda trigger. Add a CloudWatch alarm when DLQ message count > 0. Silent failures — where Lambda exhausts retries and drops the event — are the worst kind of production bug. They don't page you; they just silently lose data. A DLQ alarm means you find out before your users do.",
//       },
//       { type: "h2", text: "3. Provisioned concurrency for latency-sensitive paths" },
//       {
//         type: "p",
//         text: "Cold starts add 1-3 seconds on first invocation. For async background tasks that's fine. For synchronous API paths it's not. Provisioned concurrency keeps a pool of pre-initialised Lambda instances warm. We apply it only to the 2-3 functions in the critical path — it costs money, so be selective.",
//       },
//       {
//         type: "blockquote",
//         text: "Apply provisioned concurrency surgically — cold starts are a tax, not a crisis, for async workloads.",
//       },
//       { type: "h2", text: "4. Structured logging with correlation IDs" },
//       {
//         type: "p",
//         text: "Distributed tracing across Lambda functions is painful without a common correlation ID. Pass X-Correlation-ID through every event payload and every outbound call. Log in JSON. When something goes wrong across a chain of Lambda invocations, you grep one ID in CloudWatch Logs Insights and get the full picture in seconds.",
//       },
//       {
//         type: "list",
//         items: [
//           "Idempotency keys via DynamoDB conditional writes — do this before anything else.",
//           "SQS DLQ + CloudWatch alarm on every trigger — silent failures are the worst failures.",
//           "Provisioned concurrency only on synchronous, latency-sensitive functions.",
//           "Correlation IDs in every event payload and every log line.",
//         ],
//       },
//     ],
//   },
//   {
//     id: "ai-for-backend-engineers",
//     title: "What a backend engineer actually needs to know about AI",
//     excerpt:
//       "Cutting through the hype — the small subset of AI engineering that reliably improves backend products in 2025.",
//     date: "2024-06-17",
//     readTime: "6 min read",
//     category: "AI",
//     tags: ["AI", "Engineering"],
//     content: [
//       {
//         type: "p",
//         text: "Every backend engineer is being told they need to 'learn AI'. Most of what's being recommended is either irrelevant (transformer theory), premature (fine-tuning), or already abstracted away by good SDKs. Here's the subset that actually matters for building better backend products in 2025.",
//       },
//       { type: "h2", text: "What you don't need to know" },
//       {
//         type: "p",
//         text: "You don't need to understand attention mechanisms, backpropagation, or CUDA kernels. You don't need to fine-tune models (almost never the right answer). You don't need to run inference on your own hardware unless you have very specific latency or privacy requirements. That's all real work, but it's not backend engineering — it's ML engineering.",
//       },
//       { type: "h2", text: "Token limits and chunking" },
//       {
//         type: "p",
//         text: "Every LLM has a context window limit. If you're building retrieval-augmented generation (RAG) — feeding documents to an LLM — you need to chunk those documents correctly. Chunk too large and you exceed limits. Chunk too small and you lose context. Overlap your chunks. Use semantic chunking over fixed character counts when it matters. This is a real engineering problem with measurable quality tradeoffs.",
//       },
//       { type: "h2", text: "Latency is different" },
//       {
//         type: "p",
//         text: "LLM API calls are slow by backend standards — 2-15 seconds is normal. Design your systems for this. Stream responses to the client rather than waiting for completion. Use async processing for non-interactive workloads. Never block a request thread on a synchronous LLM call in a high-throughput service.",
//       },
//       {
//         type: "blockquote",
//         text: "LLMs are slow, expensive, and non-deterministic. Design your backend accordingly.",
//       },
//       { type: "h2", text: "Cost modelling and caching" },
//       {
//         type: "p",
//         text: "Token costs compound at scale. A feature that costs $0.002 per call sounds cheap until it's called 10 million times a month. Cache LLM outputs aggressively for identical or near-identical inputs. Use prompt compression. Route simpler requests to cheaper/faster models. These are standard backend engineering problems with a new cost structure.",
//       },
//       { type: "h2", text: "Output validation" },
//       {
//         type: "p",
//         text: "Hallucination isn't a bug you fix — it's a constraint you design around. Request structured output (JSON schema) rather than free text. Validate outputs before using them. Build fallback paths for when the model returns something unusable. The engineers building the best AI products are the ones treating LLM output like any other untrusted external API response.",
//       },
//       {
//         type: "list",
//         items: [
//           "Chunking and context management for RAG — directly affects output quality.",
//           "Async / streaming patterns for latency — don't block threads on LLM calls.",
//           "Cost modelling and output caching — token spend compounds fast.",
//           "Output validation and fallbacks — treat LLM output as untrusted.",
//         ],
//       },
//     ],
//   },
];

export const blogCategories = ["All", "Backend", "Databases", "Cloud", "AI"];

export const links = [
  {
    label: "LinkedIn",
    handle: "in/harshtripathi-in",
    href: "https://www.linkedin.com/in/harshtripathi-in",
    icon: "Linkedin",
  },
  {
    label: "GitHub",
    handle: "@harshtripathi-in",
    href: "https://github.com/harshtripathi-in",
    icon: "Github",
  },
  {
    label: "Email",
    handle: "harshtripathiyt@gmail.com",
    href: "mailto:harshtripathiyt@gmail.com",
    icon: "Mail",
  },
  {
    label: "Phone",
    handle: "+91 80813 67265",
    href: "tel:+918081367265",
    icon: "Phone",
  },
  {
    label: "Resume (PDF)",
    handle: "Download full CV",
    href: "../DOC/Harsh_Tripathi_Resume_.pdf",
    icon: "FileText",
  },
  {
    label: "AWS Certification",
    handle: "Verify on AWS",
    href: "https://aws.amazon.com/certification/",
    icon: "Award",
  },
];
